import type React from "react"
import { useEffect } from "react"
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom"
import "./globals.css"
import { LanguageProvider } from "@/contexts/language-context"
import { AuthProvider } from "@/contexts/auth-context"
import { CodeProvider } from "@/contexts/code-context"
import { Toaster } from '@/components/ui/toaster'
// Note: Theme switching removed during Vite migration. Can be re-added if needed. 
// consider using a React-only solution like context + localStorage
import { FeatureFlagProvider } from '@/contexts/feature-flag-context'
import { RouteTracker } from '@/components/route-tracker'
import EngagementListPage from '@/pages/EngagementListPage'
import EngagementDetailsPage from '@/pages/EngagementDetailsPage'
import EngagementCreatePage from '@/pages/EngagementCreatePage'
import PortfolioDashboardPage from '@/pages/PortfolioDashboardPage'
import ResourceAvailabilityPage from '@/pages/ResourceAvailabilityPage'
import ResourceAvailabilityWorkerPage from '@/pages/ResourceAvailabilityWorkerPage'

function NavigationListener() {
  const navigate = useNavigate()

  useEffect(() => {
    const handleParentMessage = (event: MessageEvent) => {

      try {
        // Validate payload structure
        const data = event.data as { action?: string; path?: unknown } | null
        
        if (!data || typeof data !== 'object') return
        
        // Only handle NAVIGATE messages with a string path
        if (data.action === 'NAVIGATE' && typeof data.path === 'string') {
          navigate(data.path, { replace: true })
        }
      } catch (error) {
        console.error('Error handling parent message:', error)
      }
    }

    window.addEventListener('message', handleParentMessage)
    
    return () => {
      window.removeEventListener('message', handleParentMessage)
    }
  }, [])

  return null
}

export default function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <CodeProvider>
            <FeatureFlagProvider>
              <div className="light">
                <NavigationListener />
                <RouteTracker />
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<PortfolioDashboardPage />} />
                  <Route path="/availability" element={<ResourceAvailabilityPage />} />
                  <Route path="/availability/:userProfileId" element={<ResourceAvailabilityWorkerPage />} />
                  <Route path="/create" element={<EngagementCreatePage />} />
                  <Route path="/:id" element={<EngagementDetailsPage />} />
                  <Route path="/:id/version/:version" element={<EngagementDetailsPage />} />
                </Routes>
                <Toaster />
              </div>
            </FeatureFlagProvider>
          </CodeProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  )
}
